import './ResourceGrid.scss';

import classNames from 'classnames';
import { Container } from 'react-bootstrap';
import { faArrowDown, faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MarketingSectionIntro } from '@components/marketing/MarketingSectionIntro/MarketingSectionIntro';

import type {
  ResourceCardProps,
  ResourceGridBlockProps,
  ResourceGridProps,
  ResourceGridSectionProps,
} from './ResourceGrid.types';

const getLinkProps = (href: string, openInNewTab?: boolean) => {
  const isExternal = href.startsWith('http');
  const isMailto = href.startsWith('mailto:');

  return {
    ...((openInNewTab ?? (isExternal && !isMailto)) ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {}),
  };
};

const ResourceCard = ({ variant, title, description, ctaLabel, href, kind, icon, openInNewTab }: ResourceCardProps) => {
  const linkProps = getLinkProps(href, openInNewTab);

  if (variant === 'fvt') {
    return (
      <a href={href} className={classNames('resource-card', 'resource-card--fvt')} {...linkProps}>
        <div className='resource-card__top'>
          {icon && (
            <span className='resource-card__icon' aria-hidden='true'>
              <FontAwesomeIcon icon={icon} />
            </span>
          )}
          {kind && <span className='resource-card__kind'>{kind}</span>}
        </div>
        <h4 className='resource-card__title'>{title}</h4>
        <p className='resource-card__body'>{description}</p>
        <span className='resource-card__cta'>
          {ctaLabel}
          <FontAwesomeIcon icon={ctaLabel === 'Download' ? faArrowDown : faArrowUpRightFromSquare} aria-hidden='true' />
        </span>
      </a>
    );
  }

  return (
    <article className={classNames('resource-card', 'resource-card--hcp')}>
      <h3 className='resource-card__title'>{title}</h3>
      <p className='resource-card__body'>{description}</p>
      <a href={href} className='resource-card__cta' {...linkProps}>
        {ctaLabel}
      </a>
    </article>
  );
};

export const ResourceGrid = ({ variant, items, links, className }: ResourceGridProps) => (
  <div className={classNames('resource-grid', `resource-grid--${variant}`, className)}>
    {items.map(item => {
      const href = links[item.slug] ?? '#';
      const isExternal = href.startsWith('mailto:') || href.startsWith('http');

      return (
        <ResourceCard
          key={item.slug}
          variant={variant}
          title={item.title}
          description={item.description}
          ctaLabel={item.ctaLabel}
          href={href}
          kind={item.kind}
          icon={item.icon}
          openInNewTab={isExternal && !href.startsWith('mailto:')}
        />
      );
    })}
  </div>
);

export const ResourceGridBlock = (props: ResourceGridBlockProps) => {
  const { variant, heading, lead, items, links, className } = props;

  return (
    <div className={classNames('resource-grid-block', className)}>
      <h3>{heading}</h3>
      {lead && <p className='resource-grid-block__lead'>{lead}</p>}
      <ResourceGrid variant={variant} items={items} links={links} />
    </div>
  );
};

export const ResourceGridSection = ({
  variant,
  items,
  links,
  sectionId,
  headingId,
  heading,
  sub,
  className,
}: ResourceGridSectionProps) => (
  <section
    id={sectionId}
    className={classNames('resource-grid-section', `resource-grid-section--${variant}`, className)}
    aria-labelledby={headingId}
  >
    <Container fluid='xxl'>
      <MarketingSectionIntro variant='plain' heading={heading} headingId={headingId}>
        {sub && <p>{sub}</p>}
      </MarketingSectionIntro>
      <ResourceGrid variant={variant} items={items} links={links} />
    </Container>
  </section>
);
